import { Link, Navigate, useNavigate } from "react-router-dom";
import { QrCode, MapTrifold, Lightning, Crown, Ticket, ChartLineUp, Fire, Martini, UsersThree, DownloadSimple } from "@phosphor-icons/react";
import { useState } from "react";
import { toast } from "sonner";
import { useAuth, homeFor } from "../context/AuthContext";
import { useInstallPrompt } from "../hooks/useInstallPrompt";
import { formatPlanPrice, usePricing } from "../hooks/usePricing";
import { usePublicLocation } from "../hooks/usePublicCity";

const HERO = "https://images.pexels.com/photos/2114365/pexels-photo-2114365.jpeg";

const FEATURES = [
  { icon: QrCode, title: "The NITEPASS QR", body: "One dynamic pass. Scan at the door and unlock free entry, drinks, VIP line and NITE POINTS instantly.", color: "text-neon-gold" },
  { icon: MapTrifold, title: "Live NITE MAP", body: "A dark neon map of the city. See where the energy is moving before you commit.", color: "text-neon-violet" },
  { icon: Fire, title: "NITE PULSE", body: "A live pulse score built from verified check-ins. No more 'trust me, it's packed'.", color: "text-neon-yellow" },
  { icon: Lightning, title: "NITE DROPS", body: "Venues release limited rewards in real time. First 25 scanned get free entry + a drink.", color: "text-neon-green" },
  { icon: Crown, title: "NITE STATUS", body: "Member → Insider → VIP → Elite → Icon. Cross-venue loyalty that follows you everywhere.", color: "text-neon-purple" },
  { icon: Ticket, title: "Digital Guest Lists", body: "Promoters send passes straight to your account. No screenshots. No arguing at the door.", color: "text-neon-gold" },
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { canInstall, installed, isIOS, install } = useInstallPrompt();
  const location = usePublicLocation();
  const publicCity = location.city;
  const pricing = usePricing(location.has_location ? location.city : undefined);
  const [findQ, setFindQ] = useState("");

  const downloadApp = async () => {
    if (installed) {
      toast.success("NITEPASS is already installed on this device");
      return;
    }
    if (canInstall) {
      await install();
      return;
    }
    if (isIOS) {
      toast.info("On iPhone: tap Share in Safari, then \"Add to Home Screen\" to install NITEPASS.");
      return;
    }
    toast.info("Open your browser menu and choose \"Install app\" or \"Add to Home Screen\".");
  };
  if (user === null) return <div className="min-h-screen bg-[#050505]" />;
  if (user) return <Navigate to={homeFor(user)} replace />;
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="fixed inset-x-0 top-0 z-50 glass"
        style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" data-testid="landing-logo" className="block transition-transform duration-200 hover:scale-[1.03]">
            <img src="/logo.png" alt="NITEPASS" className="h-14 w-auto" />
          </Link>
          <div className="flex items-center gap-3">
            <button data-testid="download-app-btn" onClick={downloadApp}
              className="btn-ghost-neon hidden !px-5 !py-2 text-sm sm:inline-flex">
              <DownloadSimple size={15} weight="bold" />
              {installed ? "App Installed" : "Download App"}
            </button>
            {user ? (
              <button data-testid="landing-open-app-btn" className="btn-neon !px-5 !py-2 text-sm"
                onClick={() => navigate(homeFor(user))}>Open App</button>
            ) : (
              <>
                <Link data-testid="landing-login-link" to="/auth" className="btn-ghost-neon !px-5 !py-2 text-sm">Log in</Link>
                <Link data-testid="landing-get-pass-btn" to="/auth?mode=register" className="btn-neon !px-5 !py-2 text-sm">Get the Pass</Link>
              </>
            )}
          </div>
        </div>
      </header>

      <section className="relative flex min-h-[92vh] items-end overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent" />
        <div className="absolute -left-24 top-1/3 h-96 w-96 rounded-full bg-neon-gold/25 blur-[140px]" />
        <div className="absolute -right-24 top-1/4 h-96 w-96 rounded-full bg-neon-violet/20 blur-[140px]" />
        <div className="relative z-10 mx-auto w-full max-w-6xl px-6 pb-24 pt-40">
          <div className="neon-overline mb-5" data-testid="hero-overline">{publicCity} • Live tonight</div>
          <h1 className="font-display text-5xl font-black leading-[0.95] sm:text-7xl lg:text-8xl" data-testid="hero-title">
            YOUR PASS<br />TO THE <span className="text-neon-gold text-glow-gold animate-flicker">NIGHT</span>
          </h1>
          <p className="mt-6 max-w-xl text-base text-zinc-400 md:text-lg">
            One nightlife dashboard. See what's happening around you right now, scan in with your
            NITEPASS QR, and earn rewards across every partner venue in the city.
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            <Link data-testid="hero-cta-guest" to="/auth?mode=register" className="btn-neon">Get the Pass — Free</Link>
            <Link data-testid="hero-cta-map" to="/auth" className="btn-ghost-neon">Explore the NITE MAP</Link>
          </div>
          <form data-testid="hero-find-promoter" className="mt-6 flex max-w-md items-center gap-2"
            onSubmit={(e) => { e.preventDefault(); navigate(`/promoters?q=${encodeURIComponent(findQ.trim())}`); }}>
            <div className="flex flex-1 items-center gap-2 rounded-full border border-white/10 bg-black/60 px-5 py-2.5 backdrop-blur">
              <UsersThree size={16} className="shrink-0 text-neon-violet" />
              <input data-testid="hero-find-promoter-input" value={findQ} onChange={(e) => setFindQ(e.target.value)}
                placeholder="Find a promoter by name or @handle"
                className="w-full bg-transparent text-sm outline-none placeholder:text-zinc-600" />
            </div>
            <button type="submit" data-testid="hero-find-promoter-btn"
              className="rounded-full border border-neon-violet/50 px-5 py-2.5 text-xs font-black uppercase tracking-wider text-neon-violet transition-colors duration-200 hover:bg-neon-violet hover:text-black">
              Search
            </button>
          </form>
          <div className="mt-3 text-xs font-bold">
            <Link to="/venues" data-testid="hero-browse-venues"
              className="text-zinc-400 underline-offset-4 transition-colors duration-200 hover:text-neon-gold hover:underline">
              Browse venues →
            </Link>
          </div>
          <div className="mt-12 flex flex-wrap items-center gap-6 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
            <span className="flex items-center gap-2"><Fire className="text-neon-gold" size={16} /> Trending</span>
            <span className="flex items-center gap-2"><Martini className="text-neon-violet" size={16} /> Drink Rewards</span>
            <span className="flex items-center gap-2"><Crown className="text-neon-yellow" size={16} /> VIP Events</span>
            <span className="flex items-center gap-2"><Lightning className="text-neon-green" size={16} /> NITE DROPS</span>
          </div>
        </div>
      </section>

      <div className="overflow-hidden border-y border-white/10 bg-[#0a0a0a] py-4">
        <div className="flex w-max animate-marquee gap-10 font-display text-sm font-bold uppercase tracking-[0.3em] text-zinc-600">
          {Array(2).fill(["NITE MAP", "NITE PULSE", "NITE DROPS", "GUEST LISTS", "NITE STATUS", "CROSS-VENUE REWARDS", "DOOR SCANNER", "PROMOTER PAYOUTS"]).flat().map((v, i) => (
            <span key={i} className="flex items-center gap-10"><span>{v}</span><span className="text-neon-gold">✦</span></span>
          ))}
        </div>
      </div>

      <section className="mx-auto max-w-6xl px-6 py-24">
        <div className="neon-overline mb-4">The experience</div>
        <h2 className="font-display text-3xl font-black sm:text-4xl">One scan handles everything</h2>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f) => (
            <div key={f.title} className="card-neon p-7" data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <f.icon size={30} weight="duotone" className={f.color} />
              <h3 className="mt-5 font-display text-lg font-bold">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-zinc-400">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-white/10 bg-[#0a0a0a]">
        <div className="mx-auto max-w-6xl px-6 py-24">
          <div className="neon-overline mb-4">For the business of night</div>
          <h2 className="font-display text-3xl font-black sm:text-4xl">NITEPASS for Promoters & Venues</h2>
          <p className="mt-4 max-w-xl text-sm text-zinc-400 md:text-base">
            Verified attendance, promoter attribution and automatic payouts. Finally know exactly
            who brought the crowd.
          </p>
          {pricing.launch_offer?.active && location.resolved && (
            <Link to="/venues" data-testid="launch-offer-chip"
              className="mt-6 inline-flex items-center gap-2.5 rounded-full border border-neon-gold/50 bg-neon-gold/10 px-4 py-2 shadow-glow-gold transition-transform duration-200 hover:scale-[1.03]">
              <span className="h-2 w-2 animate-pulse rounded-full bg-neon-gold" />
              <span className="text-[11px] font-black uppercase tracking-widest text-neon-gold">
                {pricing.launch_offer.city
                  ? `Founders Offer — only ${pricing.launch_offer.remaining} of ${pricing.launch_offer.cap} spots left in ${pricing.launch_offer.city}`
                  : `Founders Offer — ${pricing.launch_offer.total_redeemed || 0} founding venues claimed worldwide`}
                {" "}· venues lock in ${pricing.launch_offer.amount}/yr for life
              </span>
            </Link>
          )}
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            <div className="card-neon p-8" data-testid="plan-promoter-card">
              <div className="neon-overline !text-neon-violet">NITEPASS PROMOTER</div>
              <div className="mt-4 flex items-end gap-2">
                <span className="font-display text-5xl font-black">{formatPlanPrice(pricing.promoter_yearly)}</span>
                <span className="mb-1 text-sm text-zinc-500">/year</span>
              </div>
              <div className="mt-1 text-xs font-bold text-neon-violet" data-testid="plan-promoter-monthly">or {formatPlanPrice(pricing.promoter_monthly)}/month</div>
              <ul className="mt-6 space-y-3 text-sm text-zinc-300">
                <li>• Public promoter profile + Promoter Score</li>
                <li>• Unlimited events & digital guest lists</li>
                <li>• QR passes delivered to every guest</li>
                <li>• Verified traffic + earnings tracking</li>
              </ul>
              <Link data-testid="plan-promoter-cta" to="/auth?mode=register&role=promoter" className="btn-ghost-neon mt-8 w-full">Start Promoting</Link>
            </div>
            <div className="card-neon relative overflow-hidden border-neon-gold/40 p-8 shadow-glow-gold" data-testid="plan-venue-card">
              <div className="neon-overline !text-neon-gold">NITEPASS VENUE</div>
              {pricing.launch_offer?.active ? (
                <>
                  <div className="mt-4 flex items-end gap-2">
                    <span className="font-display text-5xl font-black text-neon-gold">${pricing.launch_offer.amount}</span>
                    <span className="mb-1 text-sm text-zinc-500">/year</span>
                    <span className="mb-1 text-sm text-zinc-600 line-through">{formatPlanPrice(pricing.venue_yearly)}</span>
                  </div>
                  <div className="mt-1 text-xs font-bold text-neon-gold" data-testid="plan-venue-founder">
                    FOUNDERS PRICE — {pricing.launch_offer.remaining}/{pricing.launch_offer.cap} spots left{pricing.launch_offer.city ? ` in ${pricing.launch_offer.city}` : ""} · locked for life
                  </div>
                </>
              ) : (
                <>
                  <div className="mt-4 flex items-end gap-2">
                    <span className="font-display text-5xl font-black">{formatPlanPrice(pricing.venue_yearly)}</span>
                    <span className="mb-1 text-sm text-zinc-500">/year</span>
                  </div>
                  {pricing.launch_offer?.city && (
                    <div className="mt-1 text-xs font-bold text-zinc-500" data-testid="plan-venue-founder-soldout">
                      Founder spots in {pricing.launch_offer.city} are gone — regular rate applies
                    </div>
                  )}
                </>
              )}
              <div className="mt-1 text-xs font-bold text-neon-gold" data-testid="plan-venue-monthly">or {formatPlanPrice(pricing.venue_monthly)}/month</div>
              <ul className="mt-6 space-y-3 text-sm text-zinc-300">
                <li>• Official nightlife storefront + NITE PULSE</li>
                <li>• Door scanner, entry points & NITE DROPS</li>
                <li>• Promoter teams + automatic payout math</li>
                <li>• Command-center analytics & night reports</li>
              </ul>
              <Link data-testid="plan-venue-cta" to="/auth?mode=register&role=venue" className="btn-neon mt-8 w-full">List Your Venue</Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 px-6 py-10 text-center text-xs text-zinc-600">
        <img src="/logo.png" alt="NITEPASS" className="mx-auto h-12 w-auto" />
        <div className="mt-4 flex items-center justify-center gap-5 text-xs text-zinc-500">
          <Link to="/about" data-testid="footer-about-link" className="transition-colors duration-200 hover:text-neon-gold">About</Link>
          <Link to="/privacy" data-testid="footer-privacy-link" className="transition-colors duration-200 hover:text-neon-gold">Privacy Policy</Link>
          <Link to="/terms" data-testid="footer-terms-link" className="transition-colors duration-200 hover:text-neon-gold">Terms of Service</Link>
          <Link to="/data-policy" data-testid="footer-data-link" className="transition-colors duration-200 hover:text-neon-gold">Data Policy</Link>
        </div>
        <p className="mt-3 flex items-center justify-center gap-2"><ChartLineUp size={14} /> The nightlife loyalty network. © 2026</p>
      </footer>
    </div>
  );
}
